import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DataGridBody,
  DataGridRow,
  DataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridCell,
  TableCellLayout,
  createTableColumn,
  Tooltip,
} from "@fluentui/react-components";
import { calculateStatus, getStatusPriority, formatDuration, formatDate, getPercentageColor, getStatusIcon } from '../../utils/syncStatusHelpers';
import { CONNECTOR_GROUPS } from '../../utils/connectorGroups';

const tooltipStyles = `
  .fui-Tooltip,
  [class*="Tooltip"] {
    background-color: #323130 !important;
    color: white !important;
    padding: 8px 12px !important;
    border-radius: 4px !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
  }
`;

function SyncHistoryDetails({ tenantInt, customerId, selectedConnector }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!tenantInt) return;

    setLoading(true);
    setError(null);

    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/syncs/history?TenantInt=${tenantInt}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch sync history');
        }
        return res.json();
      })
      .then((data) => {
        setHistory(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch sync history:', err);
        setError(err.message);
        setLoading(false);
      });
  }, [tenantInt]);

  const filteredHistory = useMemo(() => {
    if (!selectedConnector || selectedConnector === 'all') return history;

    const group = CONNECTOR_GROUPS.find(g => g.key === selectedConnector);
    if (!group || group.startedByValues.length === 0) return history;
    
    return history.filter(item => group.startedByValues.includes(item.StartedBy));
  }, [history, selectedConnector]);

  const columns = [
    createTableColumn({
      columnId: 'InstanceId',
      compare: (a, b) => (a.InstanceId || '').localeCompare(b.InstanceId || ''),
      renderHeaderCell: () => 'Instance ID',
      renderCell: (item) => (
        <TableCellLayout>{item.InstanceId || 'N/A'}</TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'StartDateUTC',
      compare: (a, b) => new Date(a.StartDateUTC) - new Date(b.StartDateUTC),
      renderHeaderCell: () => 'Start Date',
      renderCell: (item) => (
        <TableCellLayout>{formatDate(item.StartDateUTC)}</TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'EndDateUTC',
      compare: (a, b) => new Date(a.EndDateUTC) - new Date(b.EndDateUTC),
      renderHeaderCell: () => 'End Date',
      renderCell: (item) => (
        <TableCellLayout>{formatDate(item.EndDateUTC)}</TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'Duration',
      compare: (a, b) => {
        const durationA = a.StartDateUTC && a.EndDateUTC ? new Date(a.EndDateUTC) - new Date(a.StartDateUTC) : 0;
        const durationB = b.StartDateUTC && b.EndDateUTC ? new Date(b.EndDateUTC) - new Date(b.StartDateUTC) : 0;
        return durationA - durationB;
      },
      renderHeaderCell: () => 'Duration',
      renderCell: (item) => (
        <TableCellLayout>{formatDuration(item.StartDateUTC, item.EndDateUTC)}</TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'StartedBy',
      compare: (a, b) => (a.StartedBy || '').localeCompare(b.StartedBy || ''),
      renderHeaderCell: () => 'Started By',
      renderCell: (item) => (
        <TableCellLayout>{item.StartedBy || 'N/A'}</TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'ModifiedRows',
      compare: (a, b) => (a.ModifiedRows || 0) - (b.ModifiedRows || 0),
      renderHeaderCell: () => 'Modified Rows',
      renderCell: (item) => (
        <TableCellLayout>{item.ModifiedRows ?? 0}</TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'Status',
      compare: (a, b) => {
        const statusA = calculateStatus(a.SuccessfulTables || 0, a.TotalTables || 0).status;
        const statusB = calculateStatus(b.SuccessfulTables || 0, b.TotalTables || 0).status;
        return getStatusPriority(statusA) - getStatusPriority(statusB);
      },
      renderHeaderCell: () => 'Status',
      renderCell: (item) => {
        const { status } = calculateStatus(item.SuccessfulTables || 0, item.TotalTables || 0);
        const successfulTables = item.SuccessfulTables || 0;
        const totalTables = item.TotalTables || 0;
        const isInProgress = !item.EndDateUTC;
        const percentage = isInProgress ? null : (totalTables > 0 ? Math.round((successfulTables / totalTables) * 100) : null);
        const percentageColor = getPercentageColor(percentage);
        const statusIcon = getStatusIcon(status, isInProgress);
        
        return (
          <TableCellLayout className="status-column-cell">
            <Tooltip
              relationship="description"
              content={
                <div style={{
                  backgroundColor: '#323130',
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  fontSize: '14px',
                  maxWidth: '300px'
                }}>
                  {isInProgress ? 'Sync in progress' : `${successfulTables} / ${totalTables}`}
                </div>
              }
            >
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'default' }}>
                <span>{statusIcon}</span>
                {percentage !== null && (
                  <span style={{ color: percentageColor, fontWeight: '500', fontSize: '14px' }}>
                    {percentage}%
                  </span>
                )}
              </div>
            </Tooltip>
          </TableCellLayout>
        );
      }
    }),
  ];

  return (
    <div style={{ marginTop: '30px' }}>
      <style>{tooltipStyles}</style>
      <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', marginTop: '0' }}>
        Sync History {selectedConnector === 'indvidual' ? '(Last 24 Hours)' : '(Last 7 Days)'}
      </h3>
      
      {loading && <p>Loading...</p>}
      {error && <p style={{ color: 'red' }}>Error: {error}</p>}
      {!loading && !error && filteredHistory.length === 0 && (
        <p>No sync history found.</p>
      )}
      {!loading && !error && filteredHistory.length > 0 && (
        <div style={{
          border: '1px solid #e1dfdd',
          borderRadius: '4px',
          overflow: 'hidden'
        }}>
          <DataGrid
            items={filteredHistory}
            columns={columns}
            sortable
            noNativeElements={false}
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              tableLayout: 'auto'
            }}
          >
            <DataGridHeader>
              <DataGridRow style={{ borderBottom: '2px solid #e1dfdd' }}>
                {({ renderHeaderCell }) => (
                  <DataGridHeaderCell
                    style={{
                      borderRight: '1px solid #e1dfdd',
                      padding: '2px 4px',
                      backgroundColor: '#f3f2f1',
                      fontWeight: '600'
                    }}
                  >
                    {renderHeaderCell()}
                  </DataGridHeaderCell>
                )}
              </DataGridRow>
            </DataGridHeader>
            <DataGridBody>
              {({ item, rowId }) => (
                <DataGridRow
                  key={rowId}
                  style={{ borderBottom: '1px solid #e1dfdd', cursor: 'pointer' }}
                  onClick={() => {
                    const searchParams = new URLSearchParams(location.search);
                    const tabParam = searchParams.get('tab');
                    const url = `/sync-instance/${tenantInt}/${item.InstanceId}/${customerId}${tabParam ? `?tab=${tabParam}` : ''}`;
                    navigate(url, { 
                      state: { 
                        startedBy: item.StartedBy,
                        startDate: item.StartDateUTC,
                        endDate: item.EndDateUTC
                      } 
                    });
                  }}
                >
                  {({ renderCell }) => (
                    <DataGridCell
                      style={{
                        borderRight: '1px solid #e1dfdd',
                        padding: '2px 4px'
                      }}
                    >
                      {renderCell(item)}
                    </DataGridCell>
                  )}
                </DataGridRow>
              )}
            </DataGridBody>
          </DataGrid>
        </div>
      )}
    </div>
  );
}

export default SyncHistoryDetails;
